import { useState, useEffect } from 'react';
import { Modal, Alert } from 'react-bootstrap';
import { MdSave } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { AnomalyDetectionLambdaFetch, getUserFacingMessage } from '@src/utils/fetch-utils';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector } from '@src/redux/company';
import { emailSelector } from '@src/redux/user';
import './EditAlertModal.scss';
import { AlertFormData, DEFAULT_ALERT_FORM_DATA } from './anomalyDetectionConstants';
import {
  useEmailAddresses,
  useSlackMembers,
  useSlackChannels,
  AlertPreset
} from './hooks/useAlertData';
import { PresetForm } from './components/PresetForm';

interface EditPresetModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: () => void;
  preset: AlertPreset | null;
}

export const EditPresetModal = ({ show, onHide, onSubmit, preset }: EditPresetModalProps) => {
  const company = useAppSelector(currentCompanySelector);
  const email = useAppSelector(emailSelector);

  const {
    data: emailAddressOptions = [],
    isLoading: isLoadingEmailAddresses,
    error: emailAddressError,
    refetch: refetchEmails
  } = useEmailAddresses();

  const {
    data: slackMemberOptions = [],
    isLoading: isLoadingSlackMembers,
    error: slackMembersError,
    refetch: refetchSlackMembers
  } = useSlackMembers();

  const {
    data: slackChannelOptions = [],
    isLoading: isLoadingSlackChannels,
    error: slackChannelsError,
    refetch: refetchSlackChannels
  } = useSlackChannels();

  const [formData, setFormData] = useState<AlertFormData>(DEFAULT_ALERT_FORM_DATA);
  const [presetName, setPresetName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const setError = useSetError();
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    if (show && preset) {
      setFormData(preset.preset_data || DEFAULT_ALERT_FORM_DATA);
      setPresetName(preset.preset_name || '');
      setDescription(preset.description || '');
      setErrors([]);
      setShowSuccessPopup(false);

      refetchEmails();
      refetchSlackMembers();
      refetchSlackChannels();
    }
  }, [show, preset, refetchEmails, refetchSlackMembers, refetchSlackChannels]);

  const isFormValid = (): boolean => {
    if (!presetName.trim()) {
      return false;
    }

    if (!description.trim()) {
      return false;
    }

    const hasMonitoringType =
      formData.actual.enabled || formData.projected.enabled || formData.remainder.enabled;
    if (!hasMonitoringType) {
      return false;
    }

    if (formData.actual.enabled) {
      const actualUnder = formData.actual.under.replace(/[^0-9]/g, '');
      const actualOver = formData.actual.over.replace(/[^0-9]/g, '');
      if (!actualUnder || !actualOver) {
        return false;
      }
    }

    if (formData.projected.enabled) {
      const projectedUnder = formData.projected.under.replace(/[^0-9]/g, '');
      const projectedOver = formData.projected.over.replace(/[^0-9]/g, '');
      if (!projectedUnder || !projectedOver) {
        return false;
      }
    }

    if (formData.remainder.enabled) {
      const remainderUnder = formData.remainder.under.replace(/[^0-9]/g, '');
      const remainderOver = formData.remainder.over.replace(/[^0-9]/g, '');
      if (!remainderUnder || !remainderOver) {
        return false;
      }
    }

    if (!formData.email.enabled && !formData.slack.enabled) {
      return false;
    }

    if (formData.slack.enabled) {
      if (
        !formData.slack.channels ||
        formData.slack.channels.length === 0 ||
        !formData.slack.members ||
        formData.slack.members.length === 0
      ) {
        return false;
      }
    }

    if (formData.email.enabled) {
      if (!formData.email.addresses || formData.email.addresses.length === 0) {
        return false;
      }
    }

    return true;
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!presetName.trim()) {
      newErrors.push('Preset name is required');
    }

    if (!description.trim()) {
      newErrors.push('Description is required');
    }

    const hasMonitoringType =
      formData.actual.enabled || formData.projected.enabled || formData.remainder.enabled;
    if (!hasMonitoringType) {
      newErrors.push(
        'At least one monitoring type (Actual, Projected, or Remainder) must be enabled'
      );
    }

    if (formData.actual.enabled) {
      const actualUnder = formData.actual.under.replace(/[^0-9]/g, '');
      const actualOver = formData.actual.over.replace(/[^0-9]/g, '');
      if (!actualUnder) {
        newErrors.push("Actual 'Under' threshold is required");
      }
      if (!actualOver) {
        newErrors.push("Actual 'Over' threshold is required");
      }
    }

    if (formData.projected.enabled) {
      const projectedUnder = formData.projected.under.replace(/[^0-9]/g, '');
      const projectedOver = formData.projected.over.replace(/[^0-9]/g, '');
      if (!projectedUnder) {
        newErrors.push("Projected 'Under' threshold is required");
      }
      if (!projectedOver) {
        newErrors.push("Projected 'Over' threshold is required");
      }
    }

    if (formData.remainder.enabled) {
      const remainderUnder = formData.remainder.under.replace(/[^0-9]/g, '');
      const remainderOver = formData.remainder.over.replace(/[^0-9]/g, '');
      if (!remainderUnder) {
        newErrors.push("Remainder 'Under' threshold is required");
      }
      if (!remainderOver) {
        newErrors.push("Remainder 'Over' threshold is required");
      }
    }

    if (!formData.email.enabled && !formData.slack.enabled) {
      newErrors.push('At least one notification method (Slack or Email) must be enabled');
    }

    if (formData.slack.enabled) {
      if (!formData.slack.channels || formData.slack.channels.length === 0) {
        newErrors.push(
          'At least one Slack channel is required when Slack notifications are enabled'
        );
      }
      if (!formData.slack.members || formData.slack.members.length === 0) {
        newErrors.push(
          'At least one Slack member is required when Slack notifications are enabled'
        );
      }
    }

    if (formData.email.enabled) {
      if (!formData.email.addresses || formData.email.addresses.length === 0) {
        newErrors.push(
          'At least one email address is required when Email notifications are enabled'
        );
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSave = async () => {
    if (validateForm() && preset) {
      try {
        setIsSaving(true);

        const presetData = {
          company,
          alert_type: 'Budget',
          preset_name: presetName,
          description,
          preset_data: formData,
          created_by_email: email
        };

        await AnomalyDetectionLambdaFetch(`/company-alert-presets/${preset.id}`, {
          method: 'PUT',
          body: presetData
        });

        setShowSuccessPopup(true);
        onSubmit();
      } catch (error) {
        setError({
          title: "Couldn't update preset",
          message: getUserFacingMessage(error, "Couldn't update preset"),
          reportError: error
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleCancel = () => {
    onHide();
  };

  const handleSuccessClose = () => {
    setShowSuccessPopup(false);
    onHide();
    onSubmit();
  };

  const handleFormDataChange = (newFormData: AlertFormData) => {
    setFormData(newFormData);
  };

  if (!preset) {
    return null;
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size='xl' centered className='edit-alert-modal'>
        <Modal.Header>
          <Modal.Title>Edit Budget Preset</Modal.Title>
          <div className='required-fields-note'>*Required fields marked with an asterisk.</div>
        </Modal.Header>

        <Modal.Body>
          {errors.length > 0 && (
            <Alert variant='danger' className='mb-3'>
              <ul className='mb-0'>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          <PresetForm
            formData={formData}
            onFormDataChange={handleFormDataChange}
            presetName={presetName}
            onPresetNameChange={setPresetName}
            description={description}
            onDescriptionChange={setDescription}
            emailAddressOptions={emailAddressOptions}
            slackMemberOptions={slackMemberOptions}
            slackChannelOptions={slackChannelOptions}
            isLoadingEmailAddresses={isLoadingEmailAddresses}
            isLoadingSlackMembers={isLoadingSlackMembers}
            isLoadingSlackChannels={isLoadingSlackChannels}
            emailAddressError={emailAddressError}
            slackMembersError={slackMembersError}
            slackChannelsError={slackChannelsError}
          />
        </Modal.Body>

        <Modal.Footer className='modal-controls'>
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            leadingElement={<MdSave />}
            disabled={isSaving || !isFormValid()}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSuccessPopup}
        onHide={handleSuccessClose}
        size='sm'
        centered
        className='success-popup-modal'
      >
        <Modal.Body className='text-center p-4'>
          <div className='success-icon mb-3'>
            <MdSave className='success-icon-svg' />
          </div>
          <h5 className='mb-3'>Preset Updated Successfully!</h5>
          <p className='text-muted mb-4'>
            Your budget preset &quot;{presetName}&quot; has been updated and saved.
          </p>
          <Button onClick={handleSuccessClose} size={Size.SM}>
            Continue
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
};
